/**
 * Shared time-axis math for the resource-availability waterfall charts (single-worker
 * drill-down + multi-worker team view). Both charts render an axis that's monthly within
 * the heatmap window and switches to quarterly past it; quarter cells get compressed so
 * the far-future extension doesn't dominate the chart.
 */

export interface TimelineMonth {
  monthStart: string
  monthLabel: string
  isCurrentMonth: boolean
  /**
   * Optional explicit end date (inclusive). When omitted, the axis treats `monthStart`
   * as a month-start and computes end = last-day-of-that-month. Pass an explicit value
   * to use the same axis for non-month buckets (e.g. weekly cells where end = Sunday).
   */
  endIso?: string
}

export interface TimelineAxisCell {
  kind: 'month' | 'quarter'
  startIso: string
  endIso: string
  label: string
  isCurrentMonth: boolean
}

export interface BarRange {
  left: number
  width: number
}

export interface TimelineAxis {
  cells: TimelineAxisCell[]
  cellWidthsFr: number[]
  cellFrTemplate: string
  totalWidthFr: number
  firstQuarterIndex: number
  currentMonthIndex: number
  currentMonthIso: string | null
  pctFromMs: (ms: number) => number
  rangePct: (startIso: string, endIso: string) => BarRange
}

const QUARTER_COMPRESSION = 3
const DAY_MS = 86_400_000

/**
 * Build a renderable timeline axis. `maxEndIso` controls the far-right extension —
 * pass the latest engagement end across whatever rows the chart will render, or null
 * to stop at the heatmap window.
 */
export function buildTimelineAxis(months: TimelineMonth[], maxEndIso: string | null): TimelineAxis {
  const cells = buildAxisCells(months, maxEndIso)

  // Quarter cells are compressed so the far-future extension doesn't dominate. The
  // 12-month window is the user's primary focus; quarters are a secondary "this engagement
  // runs much further out" cue, so they pay a 3x penalty.
  const cellWidthsFr = cells.map(c =>
    c.kind === 'quarter' ? daysIn(c) / QUARTER_COMPRESSION : daysIn(c)
  )
  const totalWidthFr = cellWidthsFr.reduce((s, w) => s + w, 0)

  function pctFromMs(ms: number): number {
    if (totalWidthFr <= 0) return 0
    let cumulative = 0
    for (let i = 0; i < cells.length; i++) {
      const cell = cells[i]
      const cellStartMs = utcMs(cell.startIso)
      const cellEndMsExclusive = utcMs(cell.endIso) + DAY_MS
      const w = cellWidthsFr[i]
      if (ms < cellStartMs) return (cumulative / totalWidthFr) * 100
      if (ms < cellEndMsExclusive) {
        const fraction = (ms - cellStartMs) / (cellEndMsExclusive - cellStartMs)
        return ((cumulative + fraction * w) / totalWidthFr) * 100
      }
      cumulative += w
    }
    return 100
  }

  function rangePct(startIso: string, endIso: string): BarRange {
    const left = clampPct(pctFromMs(utcMs(startIso)))
    const right = clampPct(pctFromMs(utcMs(endIso) + DAY_MS))
    return { left, width: Math.max(0, right - left) }
  }

  const firstQuarterIndex = cells.findIndex(c => c.kind === 'quarter')
  const currentMonthIndex = cells.findIndex(c => c.kind === 'month' && c.isCurrentMonth)
  const currentMonthIso = currentMonthIndex >= 0 ? cells[currentMonthIndex].startIso : null
  const cellFrTemplate = cellWidthsFr.map(w => `${w}fr`).join(' ')

  return {
    cells,
    cellWidthsFr,
    cellFrTemplate,
    totalWidthFr,
    firstQuarterIndex,
    currentMonthIndex,
    currentMonthIso,
    pctFromMs,
    rangePct,
  }
}

function buildAxisCells(months: TimelineMonth[], maxEndIso: string | null): TimelineAxisCell[] {
  // Detect whether the caller supplied non-month buckets (e.g. weekly) by checking for
  // explicit endIso values. Used to skip the month-aligned quarter trim, which would
  // otherwise pop up to 12 weekly cells.
  const hasExplicitEnds = months.some(m => !!m.endIso)
  const cells: TimelineAxisCell[] = months.map(m => ({
    kind: 'month',
    startIso: m.monthStart,
    endIso: m.endIso ?? lastDayOfMonth(m.monthStart),
    label: m.monthLabel,
    isCurrentMonth: m.isCurrentMonth,
  }))
  if (cells.length === 0) return cells
  // No extension to render — keep all buckets as supplied.
  if (!maxEndIso) return cells
  const lastMonthEndMs = utcMs(cells[cells.length - 1].endIso)
  const maxRowEndMs = utcMs(maxEndIso)
  if (maxRowEndMs <= lastMonthEndMs) return cells

  // Extension is needed. For month-aligned input, drop 0–2 trailing months so the first
  // quarter cell starts on a calendar-quarter boundary (Apr/Jul/Oct/Jan). Skip this trim
  // for weekly (or any non-month) input — the first appended quarter may be partial but
  // we'd otherwise pop a full quarter's worth of weekly fidelity.
  if (!hasExplicitEnds) {
    // Trim into a copy first; if it would empty the array (single-month input not on a
    // quarter-end, e.g. just May), keep the original months and accept a non-aligned
    // first quarter. Showing the user's selected months + a partial quarter beats showing
    // nothing — the prior behaviour returned an empty cells[] which renders the whole
    // waterfall as null.
    const trimmed = [...cells]
    while (trimmed.length > 0) {
      const lastMonthIdx = new Date(trimmed[trimmed.length - 1].startIso + 'T00:00:00Z').getUTCMonth()
      if (lastMonthIdx % 3 === 2) break
      trimmed.pop()
    }
    if (trimmed.length > 0) {
      cells.length = 0
      cells.push(...trimmed)
    }
    // else: leave `cells` untouched — single-month input keeps that month.
  }

  // Append from the day after the last cell's end so we don't double-count.
  let cursor = nextDayIso(cells[cells.length - 1].endIso)
  while (utcMs(cursor) <= maxRowEndMs) {
    const qEnd = quarterEnd(cursor)
    cells.push({
      kind: 'quarter',
      startIso: cursor,
      endIso: qEnd,
      label: quarterLabel(cursor),
      isCurrentMonth: false,
    })
    cursor = addMonths(qEnd, 1)
  }
  return cells
}

function nextDayIso(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z')
  return formatIsoDate(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1)))
}

export function utcMs(iso: string): number {
  return new Date(iso + 'T00:00:00Z').getTime()
}

function daysIn(cell: TimelineAxisCell): number {
  return Math.round((utcMs(cell.endIso) - utcMs(cell.startIso)) / DAY_MS) + 1
}

function lastDayOfMonth(monthStartIso: string): string {
  const d = new Date(monthStartIso + 'T00:00:00Z')
  return formatIsoDate(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)))
}

function quarterEnd(quarterStartIso: string): string {
  const d = new Date(quarterStartIso + 'T00:00:00Z')
  return formatIsoDate(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 3, 0)))
}

function quarterLabel(quarterStartIso: string): string {
  const d = new Date(quarterStartIso + 'T00:00:00Z')
  const q = Math.floor(d.getUTCMonth() / 3) + 1
  const yy = d.getUTCFullYear().toString().slice(2)
  return `Q${q} '${yy}`
}

export function addMonths(iso: string, n: number): string {
  const d = new Date(iso + 'T00:00:00Z')
  return formatIsoDate(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, 1)))
}

/** Whole-month diff: 2026-04-01 → 2026-04-30 = 0; 2026-04-01 → 2026-05-31 = 1; past dates negative. */
export function monthsBetween(fromIso: string, toIso: string): number {
  const from = new Date(fromIso + 'T00:00:00Z')
  const to = new Date(toIso + 'T00:00:00Z')
  return (to.getUTCFullYear() - from.getUTCFullYear()) * 12 + (to.getUTCMonth() - from.getUTCMonth())
}

function formatIsoDate(d: Date): string {
  const yyyy = d.getUTCFullYear().toString().padStart(4, '0')
  const mm = (d.getUTCMonth() + 1).toString().padStart(2, '0')
  const dd = d.getUTCDate().toString().padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

function clampPct(p: number): number {
  if (p < 0) return 0
  if (p > 100) return 100
  return p
}

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * Compact date-range label for chip rows. Folds the year onto the end date when both
 * dates share a year (`Jan 6 → Nov 28, 2026`); otherwise spells out both years.
 */
export function formatDateRange(startIso: string, endIso: string): string {
  const sd = new Date(startIso + 'T00:00:00Z')
  const ed = new Date(endIso + 'T00:00:00Z')
  const sameYear = sd.getUTCFullYear() === ed.getUTCFullYear()
  const fmt = (d: Date, withYear: boolean) =>
    withYear
      ? `${MONTH_ABBR[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`
      : `${MONTH_ABBR[d.getUTCMonth()]} ${d.getUTCDate()}`
  return sameYear ? `${fmt(sd, false)} → ${fmt(ed, true)}` : `${fmt(sd, true)} → ${fmt(ed, true)}`
}
